import { z } from "zod";
import { adminOperationsPilotSchemaVersionSchema } from "./admin-operations-core.js";
const utcDaySchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/u);
export const adminBusinessOverviewCommandSchema = z
    .object({
    from: utcDaySchema,
    to: utcDaySchema,
    environment: z.enum(["LIVE", "TEST_SANDBOX"]).default("TEST_SANDBOX"),
})
    .strict()
    .superRefine((value, context) => {
    const from = Date.parse(`${value.from}T00:00:00.000Z`);
    const to = Date.parse(`${value.to}T00:00:00.000Z`);
    if (!Number.isFinite(from) || !Number.isFinite(to) || from > to)
        context.addIssue({ code: "custom", message: "The reporting date range is invalid." });
    if (to - from > 89 * 24 * 60 * 60 * 1_000)
        context.addIssue({ code: "custom", message: "Business reporting is limited to 90 days." });
});
export const adminBusinessMetricDaySchema = z
    .object({
    date: utcDaySchema,
    checkoutsStarted: z.number().int().nonnegative(),
    paidOrders: z.number().int().nonnegative(),
    giftOrders: z.number().int().nonnegative(),
    voucherRedemptions: z.number().int().nonnegative(),
    booksCreated: z.number().int().nonnegative(),
    booksApproved: z.number().int().nonnegative(),
    grossSalesCents: z.number().int().nonnegative(),
})
    .strict();
export const adminLaunchEconomicsProfileSchema = z
    .object({
    bookFamily: z.enum(["MY_STORY", "OUR_STORY", "BEDTIME_STORY", "COMIC_BOOK", "RECIPE_BOOK"]),
    displayName: z.string().min(1).max(80),
    profileId: z.string().min(1).max(120),
    format: z.string().min(1).max(120),
    interiorPageCount: z.number().int().positive().max(200),
    evidenceKind: z.literal("ESTIMATED"),
    productCostCents: z.number().int().nonnegative(),
    standardShippingCents: z.number().int().nonnegative(),
    landedCostCents: z.number().int().nonnegative(),
    generationReserveCents: z.number().int().nonnegative(),
    paymentProcessingCents: z.number().int().nonnegative(),
    modeledDirectContributionCents: z.number().int(),
    modeledDirectMarginBasisPoints: z.number().int().min(-10_000).max(10_000),
})
    .strict();
export const adminLaunchEconomicsSchema = z
    .object({
    modelRevision: z.literal("2026-09-28.1"),
    approvedAt: z.string().datetime(),
    scope: z.literal("DIRECT_US_BOOK_VOUCHER_LAUNCH"),
    currency: z.literal("USD"),
    voucherPriceCents: z.literal(7_999),
    includedHardcoverCopies: z.literal(1),
    includedShipping: z.literal("STANDARD_US_ONLY"),
    expressShippingOffered: z.literal(false),
    providerQuoteCapturedAt: z.string().datetime(),
    providerQuoteEnvironment: z.literal("SANDBOX"),
    providerQuoteResponsesArchived: z.literal(false),
    providerQuoteUse: z.literal("REFRESH_EXACT_ADDRESS_QUOTE_BEFORE_SUBMISSION"),
    actualCostCoverage: z
        .object({
        status: z.literal("UNKNOWN"),
        completedBooks: z.literal(0),
        reasonCode: z.literal("NO_CURRENT_RELEASE_COST_COHORT"),
    })
        .strict(),
    channelRemittance: z
        .object({
        status: z.literal("UNKNOWN"),
        reasonCode: z.literal("RETAIL_TERMS_NOT_APPROVED"),
    })
        .strict(),
    profiles: z.array(adminLaunchEconomicsProfileSchema).length(5),
    costsNotIncludedInContribution: z.array(z.string().min(1).max(80)).min(1).max(20),
})
    .strict();
export const adminBusinessOverviewResponseSchema = z
    .object({
    data: z
        .object({
        schemaVersion: adminOperationsPilotSchemaVersionSchema,
        environment: z.enum(["LIVE", "TEST_SANDBOX"]),
        currency: z.literal("USD"),
        completeness: z.literal("FORWARD_ONLY"),
        trackingStartedAt: z.string().datetime(),
        from: utcDaySchema,
        to: utcDaySchema,
        totals: adminBusinessMetricDaySchema.omit({ date: true }),
        insights: z
            .object({
            checkoutConversionBasisPoints: z.number().int().min(0).max(10_000),
            averageOrderValueCents: z.number().int().nonnegative(),
            giftOrderShareBasisPoints: z.number().int().min(0).max(10_000),
            lastActivityAt: z.string().datetime().nullable(),
        })
            .strict(),
        comparison: z
            .object({
            periodDays: z.number().int().positive().max(90),
            currentGrossSalesCents: z.number().int().nonnegative(),
            previousGrossSalesCents: z.number().int().nonnegative(),
            currentPaidOrders: z.number().int().nonnegative(),
            previousPaidOrders: z.number().int().nonnegative(),
        })
            .strict()
            .default({
            periodDays: 1,
            currentGrossSalesCents: 0,
            previousGrossSalesCents: 0,
            currentPaidOrders: 0,
            previousPaidOrders: 0,
        }),
        economics: adminLaunchEconomicsSchema,
        // Goodwill vouchers staff sent to make things right, in the range (2026-10-21).
        goodwill: z
            .object({
            vouchers: z.number().int().nonnegative(),
            faceValueCents: z.number().int().nonnegative(),
            truncated: z.boolean(),
        })
            .strict()
            .optional(),
        days: z.array(adminBusinessMetricDaySchema).max(90),
    })
        .strict(),
})
    .strict();
//# sourceMappingURL=admin-operations-business.js.map